import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Typography, Box, Card, CardContent, Grid, Chip, Button,
  Alert, Snackbar, Skeleton,
} from '@mui/material';
import { GitHub, Add, Assessment, ArrowForward, PlayArrow, EnergySavingsLeaf, Refresh } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import { updateUser } from '../../store/slices/authSlice';
import {
  useGetReposQuery,
  useLazyGetScanQuery,
  useTriggerScanMutation,
  useUpdateRepoMutation,
} from '../../store/api/githubApi';
import { useVerifyPaymentSessionMutation, useGetMeQuery } from '../../store/api/authApi';
import { RepositoryCard } from '../../components/repositories/RepositoryCard';
import { HealthTrendCard } from '../../components/dashboard/HealthTrendCard';
import { useWebSocket, WsEvent } from '../../hooks/useWebSocket';

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number | null): string {
  if (score === null || score === 0) return '#94a3b8';
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
}

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// ── Hero ScoreRing ────────────────────────────────────────────────────────────

const ScoreRing: React.FC<{ score: number | null; size?: number; strokeWidth?: number }> = ({
  score, size = 90, strokeWidth = 7,
}) => {
  const color = scoreColor(score);
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = score !== null ? (score / 100) * circ : 0;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="#f1f5f9" strokeWidth={strokeWidth}
        />
        {score !== null && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
        )}
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
            fontSize: size * 0.32,
            fontWeight: 800,
            lineHeight: 1,
            color: score !== null ? '#0f172a' : '#94a3b8',
          }}
        >
          {score !== null ? score : '—'}
        </Typography>
      </Box>
    </Box>
  );
};

// ── StatCard ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
  action?: { label: string; onClick: () => void };
  loading?: boolean;
}> = ({ label, value, sub, accent = '#2563eb', action, loading }) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '16px',
      boxShadow: '0 4px 16px -2px rgba(15, 23, 42, 0.04)',
      height: '100%',
      transition: 'transform 180ms ease, box-shadow 180ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.08)',
      },
    }}
  >
    <CardContent sx={{ p: 2.75, '&:last-child': { pb: 2.75 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        {action && (
          <Button
            size="small"
            onClick={action.onClick}
            endIcon={<ArrowForward sx={{ fontSize: 13, color: '#2563eb' }} />}
            sx={{
              fontSize: 12,
              color: '#2563eb',
              fontWeight: 600,
              p: 0,
              minWidth: 0,
              lineHeight: 1.2,
              textTransform: 'none',
              '&:hover': { background: 'none', color: '#1d4ed8' },
            }}
          >
            {action.label}
          </Button>
        )}
      </Box>
      {loading ? (
        <Skeleton variant="text" width={60} height={48} sx={{ bgcolor: '#f1f5f9' }} />
      ) : (
        <Typography variant="h3" fontWeight={800} sx={{ color: accent, lineHeight: 1.1, my: 0.5, fontFamily: '"JetBrains Mono", monospace' }}>
          {value}
        </Typography>
      )}
      {sub && <Typography variant="caption" sx={{ color: '#64748b', fontSize: 12 }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

// ── DashboardPage ─────────────────────────────────────────────────────────────

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, accessToken } = useSelector((state: RootState) => state.auth);
  const { data: repos, isLoading, error, refetch } = useGetReposQuery();
  const [getScan] = useLazyGetScanQuery();
  const [triggerScan] = useTriggerScanMutation();
  const [updateRepo] = useUpdateRepoMutation();
  const [verifyPaymentSession] = useVerifyPaymentSessionMutation();
  const { refetch: refetchMe } = useGetMeQuery();
  const [upgradeSnackbar, setUpgradeSnackbar] = useState(false);
  const [topupSnackbar, setTopupSnackbar] = useState(false);

  // Scan tracking per repo
  const [scanStates, setScanStates] = useState<Record<number, { status: string; scanId: number | null; message?: string }>>({});

  const updateScanState = useCallback((scanId: number, status: string, message?: string) => {
    setScanStates((prev) => {
      const repoId = Object.keys(prev).find((id) => prev[Number(id)].scanId === scanId);
      if (!repoId) return prev;
      return {
        ...prev,
        [Number(repoId)]: { ...prev[Number(repoId)], status, message },
      };
    });
  }, []);

  const handleScanEvent = useCallback((event: WsEvent) => {
    if (event.event === 'scan_status') {
      updateScanState(event.scan_id, event.status, event.message);
      if (['complete', 'failed'].includes(event.status)) refetch();
    } else if (event.event === 'scan_complete') {
      updateScanState(event.scan_id, 'complete', 'Scan complete');
      refetch();
    }
  }, [refetch, updateScanState]);

  useWebSocket({ userId: user?.id ?? null, token: accessToken, onMessage: handleScanEvent });

  // Websocket events are the fast path. Poll the authoritative scan record as
  // a fallback so a missed event cannot leave the dashboard showing progress.
  useEffect(() => {
    const activeScans = Object.values(scanStates).filter(
      (state) => state.scanId && !['complete', 'failed'].includes(state.status),
    );
    if (activeScans.length === 0) return;

    let cancelled = false;
    const poll = async () => {
      await Promise.all(activeScans.map(async ({ scanId }) => {
        if (!scanId) return;
        try {
          const scan = await getScan(scanId).unwrap();
          if (!cancelled && ['complete', 'failed'].includes(scan.status)) {
            updateScanState(scanId, scan.status, scan.status === 'failed' ? scan.status_message ?? 'Scan failed' : 'Scan complete');
            refetch();
          }
        } catch {
          // The websocket remains the primary path; transient polling errors are harmless.
        }
      }));
    };

    poll();
    const interval = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [getScan, refetch, scanStates, updateScanState]);

  const handleScan = async (repoId: number) => {
    try {
      setScanStates((prev) => ({ ...prev, [repoId]: { status: 'queued', scanId: null } }));
      const res = await triggerScan({ repo_id: repoId }).unwrap();
      if (res.scan_id) {
        setScanStates((prev) => ({ ...prev, [repoId]: { status: 'scanning', scanId: res.scan_id } }));
      }
    } catch {
      setScanStates((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
    }
  };

  const handleTogglePrGate = async (repoId: number, enabled: boolean) => {
    try {
      await updateRepo({ repo_id: repoId, pr_gate_enabled: enabled }).unwrap();
    } catch { /* ignore */ }
  };

  const syncPlanToStore = (plan: string) => {
    dispatch(updateUser({ plan: plan as 'free' | 'pro' | 'enterprise' }));
    const stored = localStorage.getItem('user');
    if (stored) {
      try { localStorage.setItem('user', JSON.stringify({ ...JSON.parse(stored), plan })); }
      catch { /* ignore */ }
    }
  };

  useEffect(() => {
    if (searchParams.get('topup') === 'success') {
      setTopupSnackbar(true);
      searchParams.delete('topup');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const upgraded = searchParams.get('upgraded');
    if (!sessionId || !upgraded || !user?.id) return;

    verifyPaymentSession({ session_id: sessionId, user_id: user.id, email: user.email ?? undefined })
      .unwrap()
      .then(() => {
        syncPlanToStore('pro');
        setUpgradeSnackbar(true);
      })
      .catch(async () => {
        try {
          const fresh = await refetchMe().unwrap();
          syncPlanToStore(fresh.plan);
          if (fresh.plan !== 'free') setUpgradeSnackbar(true);
        } catch { /* ignore */ }
      })
      .finally(() => {
        searchParams.delete('session_id');
        searchParams.delete('upgraded');
        setSearchParams(searchParams, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  const scannedRepos = (repos ?? []).filter((r) => r.latest_debt_score !== null);
  const avgScore = scannedRepos.length > 0
    ? Math.round(scannedRepos.reduce((sum, r) => sum + (r.latest_debt_score ?? 0), 0) / scannedRepos.length)
    : null;

  const totalCritical = scannedRepos.reduce((s, r) => s + (r.latest_count_critical ?? 0), 0);
  const totalHigh = scannedRepos.reduce((s, r) => s + (r.latest_count_high ?? 0), 0);
  const totalMedium = scannedRepos.reduce((s, r) => s + (r.latest_count_medium ?? 0), 0);
  const needsAttention = scannedRepos.filter((r) => (r.latest_debt_score ?? 0) < 60).length;
  const totalCarbonCo2g = scannedRepos.reduce((s, r) => s + ((r as any).latest_carbon_co2g_total ?? 0), 0);
  const hasCarbonData = totalCarbonCo2g > 0;

  const sortedRepos = [
    ...scannedRepos.slice().sort((a, b) => (a.latest_debt_score ?? 0) - (b.latest_debt_score ?? 0)),
    ...(repos ?? []).filter((r) => r.latest_debt_score === null),
  ];

  const firstTime = !isLoading && (repos ?? []).length === 0;

  return (
    <Box>
      {/* 1. Header & Welcome Line */}
      <Box sx={{ mb: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', letterSpacing: '-0.015em' }}>
            Good {timeOfDay()}, {user.github_login}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Here's the state of your codebase health
          </Typography>
        </Box>
        <Chip
          label={user.plan === 'free' ? 'Free Plan' : 'Pro'}
          size="small"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            bgcolor: '#ffffff',
            color: '#2563eb',
            border: '1px solid #cbd5e1',
            borderRadius: '10px',
            px: 1,
            py: 1.5,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '14px' }}>
          Failed to load repositories.
        </Alert>
      )}

      <Grid container spacing={3}>

        {/* 2. Portfolio Health Hero Card */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)',
              overflow: 'hidden',
              transition: 'transform 180ms ease, box-shadow 180ms ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.08)',
              },
            }}
          >
            <CardContent sx={{ p: 3.5, '&:last-child': { pb: 3.5 } }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={90} height={90} sx={{ bgcolor: '#f1f5f9' }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={40} sx={{ bgcolor: '#f1f5f9' }} />
                    <Skeleton variant="text" width="30%" height={24} sx={{ bgcolor: '#f1f5f9' }} />
                    <Skeleton variant="text" width="50%" height={28} sx={{ mt: 1, bgcolor: '#f1f5f9' }} />
                  </Box>
                </Box>
              ) : firstTime ? (
                /* Zero state */
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                  <GitHub sx={{ fontSize: 52, color: '#94a3b8' }} />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a' }} gutterBottom>
                      Connect your first repository
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3, maxWidth: 440 }}>
                      KodeGauge scans your codebase for technical debt, security vulnerabilities, and
                      infrastructure waste — then shows you exactly where to invest to get the most back.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate('/repositories')}
                      size="large"
                      sx={{ borderRadius: '8px', bgcolor: '#2563eb', color: '#ffffff', fontWeight: 700, textTransform: 'none' }}
                    >
                      Add Repository
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {/* Score ring */}
                  <ScoreRing score={avgScore} size={90} strokeWidth={7} />

                  {/* Context */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', mb: 0.5 }}>
                      Portfolio Health
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1.75 }}>
                      {scannedRepos.length} of {(repos ?? []).length} repos scanned
                      {avgScore !== null && ` · avg score ${avgScore}/100`}
                    </Typography>

                    {/* Issue pills */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {totalCritical > 0 && (
                        <Chip
                          label={`🔴 ${totalCritical} critical`}
                          size="small"
                          sx={{ bgcolor: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', fontWeight: 700, fontSize: 11, border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px' }}
                        />
                      )}
                      {totalHigh > 0 && (
                        <Chip
                          label={`🟠 ${totalHigh} high`}
                          size="small"
                          sx={{ bgcolor: 'rgba(249, 115, 22, 0.08)', color: '#f97316', fontWeight: 700, fontSize: 11, border: '1px solid rgba(249, 115, 22, 0.25)', borderRadius: '8px' }}
                        />
                      )}
                      {totalMedium > 0 && (
                        <Chip
                          label={`🟡 ${totalMedium} medium`}
                          size="small"
                          sx={{ bgcolor: 'rgba(245, 158, 11, 0.08)', color: '#f59e0b', fontWeight: 700, fontSize: 11, border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '8px' }}
                        />
                      )}
                      {totalCritical === 0 && totalHigh === 0 && totalMedium === 0 && scannedRepos.length > 0 && (
                        <Chip
                          label="✓ No critical issues"
                          size="small"
                          sx={{ bgcolor: 'rgba(34, 197, 94, 0.08)', color: '#166534', fontWeight: 700, fontSize: 11, border: '1px solid rgba(34, 197, 94, 0.25)', borderRadius: '8px' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* CTAs */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<Assessment />}
                      onClick={() => navigate('/repositories')}
                      sx={{
                        borderRadius: '8px',
                        px: 2.5,
                        py: 1,
                        bgcolor: '#2563eb',
                        color: '#ffffff',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
                        '&:hover': {
                          bgcolor: '#1d4ed8',
                          boxShadow: '0 4px 8px rgba(37, 99, 235, 0.3)',
                        },
                      }}
                    >
                      View Repositories
                    </Button>
                    {(repos ?? []).some((r) => r.latest_debt_score === null) && (
                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<PlayArrow />}
                        onClick={() => navigate('/repositories')}
                        sx={{
                          borderRadius: '8px',
                          borderColor: '#cbd5e1',
                          color: '#334155',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#2563eb',
                            bgcolor: 'rgba(37, 99, 235, 0.04)',
                            color: '#2563eb',
                          },
                        }}
                      >
                        Scan Remaining
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Health Trend Card (Requirement 1: below Portfolio Health, before summary cards) */}
        {!firstTime && (
          <Grid item xs={12}>
            <HealthTrendCard repos={repos ?? []} />
          </Grid>
        )}

        {/* 3. Three Summary Cards */}
        {!firstTime && (
          <>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Repositories"
                value={isLoading ? '—' : (repos?.length ?? 0)}
                sub={`${scannedRepos.length} scanned`}
                accent="#0f172a"
                loading={isLoading}
                action={{ label: 'Add repo', onClick: () => navigate('/repositories') }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Critical & High Issues"
                value={isLoading ? '—' : totalCritical + totalHigh}
                sub="across all scanned repos"
                accent={totalCritical + totalHigh > 0 ? '#ef4444' : '#22c55e'}
                loading={isLoading}
                action={{ label: 'View repos', onClick: () => navigate('/repositories') }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Repos Needing Attention"
                value={isLoading ? '—' : needsAttention}
                sub="score below 60"
                accent={needsAttention > 0 ? '#f59e0b' : '#22c55e'}
                loading={isLoading}
                action={{ label: 'View all', onClick: () => navigate('/repositories') }}
              />
            </Grid>

            {/* 4. Carbon & Energy Impact Tile */}
            {(hasCarbonData || (!isLoading && scannedRepos.length > 0)) && (
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid rgba(34, 197, 94, 0.25)',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px -2px rgba(15, 23, 42, 0.04)',
                    px: 3,
                    py: 2.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    flexWrap: 'wrap',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'rgba(34, 197, 94, 0.4)',
                      boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: '50%',
                      bgcolor: 'rgba(34, 197, 94, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <EnergySavingsLeaf sx={{ fontSize: 28, color: '#16a34a' }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#0f172a' }}>
                      Carbon &amp; Energy Impact
                    </Typography>
                    {hasCarbonData ? (
                      <Typography variant="body2" sx={{ color: '#475569', mt: 0.25 }}>
                        Fixing all flagged complexity issues across your repos could save an estimated{' '}
                        <strong style={{ color: '#16a34a' }}>
                          {totalCarbonCo2g >= 1000
                            ? `${(totalCarbonCo2g / 1000).toFixed(1)} kg`
                            : `${Math.round(totalCarbonCo2g)} g`} CO₂/month
                        </strong>{' '}
                        — equivalent to greener code at scale.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#475569', mt: 0.25 }}>
                        Run a scan to see the estimated carbon footprint of your codebase and how much fixing issues could save.
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      bgcolor: 'rgba(34, 197, 94, 0.08)',
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                      color: '#166534',
                      borderRadius: '10px',
                      px: 1.25,
                      py: 0.75,
                      fontWeight: 700,
                      fontSize: 10,
                    }}
                  >
                    Based on Green Software Foundation SCI
                  </Typography>
                </Card>
              </Grid>
            )}
          </>
        )}

        {/* 5. Repositories Section (Directly Below Carbon & Energy Impact) */}
        {!isLoading && !firstTime && sortedRepos.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a' }}>
                  Repositories
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Connect repos and trigger AI-powered debt scans
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => refetch()}
                  sx={{ color: '#475569', borderColor: '#cbd5e1', textTransform: 'none', fontSize: 13, fontWeight: 600 }}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/repositories')}
                  sx={{ bgcolor: '#2563eb', color: '#ffffff', textTransform: 'none', fontSize: 13, fontWeight: 700, borderRadius: '8px' }}
                >
                  Add Repo
                </Button>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {sortedRepos.map((repo) => (
                <Grid item xs={12} sm={6} md={4} key={repo.id}>
                  <RepositoryCard
                    repository={repo}
                    onScan={handleScan}
                    scanStatus={scanStates[repo.id]?.status}
                    scanMessage={scanStates[repo.id]?.message}
                    latestScanId={repo.latest_scan_id}
                    onTogglePrGate={handleTogglePrGate}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

      </Grid>

      <Snackbar
        open={upgradeSnackbar}
        autoHideDuration={6000}
        onClose={() => setUpgradeSnackbar(false)}
        message="Welcome to KodeGauge Pro! Your plan has been upgraded."
      />
      <Snackbar
        open={topupSnackbar}
        autoHideDuration={6000}
        onClose={() => setTopupSnackbar(false)}
        message="10 fix credits added to your account. Happy fixing!"
      />
    </Box>
  );
};
